import { Box, Skeleton, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { KitButton, KitCard, KitItem, KitList, SecH } from '../common/MobileKit';
import MobileSheet from '../common/MobileSheet';
import { protoIcon } from '../common/ProtoIcon';
import { KitBanner, KitEmpty, KitFormBar, KitKV } from '../common/MobileKitExtras';
import MobileItemSearch from '../common/MobileItemSearch';
import { useAppBarAction, useAppBarSubtitle, useAppBarTitle } from '../layout/AppBarActions';
import { vehicleLabel } from '../serviceorder/orderMobileUtils';
import { quoteStatusKey, quoteStatusView, quoteValidUntil } from './quoteMobileUtils';

/**
 * Operação do orçamento (itens) no shell mobile — `prototipos-mobile/orcamentos/operacao.html`.
 * Só apresentação: itens, totais, ações e permissões vêm de `pages/service_quotes/Operator.js`.
 */

const WrenchIcon = protoIcon('wrench');
const PackageIcon = protoIcon('package');
const PlusIcon = protoIcon('plus');
const CheckIcon = protoIcon('check');
const MinusIcon = protoIcon('minus');
const TagIcon = protoIcon('tag');
const TrashIcon = protoIcon('trash');
const InboxIcon = protoIcon('inbox');
const SwapIcon = protoIcon('refresh');
const DotsIcon = protoIcon('doth');

// Ícones das ações da folha "Ações" (chave vem de `pages/service_quotes/Operator.js`).
const ACTION_ICONS = {
    status: protoIcon('refresh'),
    edit: protoIcon('edit'),
    'copy-link': protoIcon('link'),
    mail: protoIcon('mail'),
    'whatsapp-send': protoIcon('msg'),
    convert: protoIcon('refresh'),
    pdf: protoIcon('download'),
    delete: protoIcon('trash'),
};

// `.sheet .action-list .item`
const ACTION_ITEM_SX = { border: 0, borderBottom: 0, p: '12px 4px' };

function ActionItem({ icon, tone, title, sub, onClick, disabled, selected }) {
    return (
        <KitItem
            icon={icon}
            tone={tone}
            title={title}
            sub={sub}
            chevron={!selected}
            end={selected ? <Box sx={{ color: 'primary.main', display: 'inline-flex' }}><CheckIcon size={20} /></Box> : undefined}
            sx={{ ...ACTION_ITEM_SX, ...(disabled ? { opacity: 0.5 } : {}) }}
            onClick={() => { if (!disabled) onClick(); }}
        />
    );
}

const lineIdOf = (item) => item.item_id ?? item.id;
const typeOf = (item) => item.type_item || item.type;

export default function MobileQuoteOperator({
    loading,
    quoteNumber,
    status,
    details,
    usesVehicle,
    quoteTotal,
    productDiscount,
    items,
    loadingItems,
    editable,
    converted,
    canChangeStatus,
    actions = [],
    statusSheetOpen,
    onStatusSheetClose,
    onChangeStatus,
    customerMessages,
    onConclude,
    onStep,
    onDiscount,
    onRemove,
    onSearch,
    searchResults,
    onAddItem,
}) {
    const theme = useTheme();
    const [actionsOpen, setActionsOpen] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [itemSheetId, setItemSheetId] = useState(null);
    const [term, setTerm] = useState('');
    const [picked, setPicked] = useState(null);
    const searchTimer = useRef(null);
    const statusKey = quoteStatusKey(status);

    useAppBarTitle(!loading && quoteNumber ? `Orçamento #${quoteNumber}` : '');
    useAppBarSubtitle('Montando itens');
    // Mesmo padrão da OS: único "Ações" (⋯) no AppBar abrindo a folha.
    useAppBarAction({ label: 'Ações', icon: DotsIcon, onClick: () => setActionsOpen(true), disabled: loading || actions.length === 0 });

    useEffect(() => () => clearTimeout(searchTimer.current), []);

    const handleTerm = (value) => {
        setTerm(value);
        setPicked(null);
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => onSearch(value.trim()), 250);
    };

    const closeAdd = () => {
        setAddOpen(false);
        setTerm('');
        setPicked(null);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <Skeleton variant="rounded" height={120} sx={{ borderRadius: '16px' }} />
                <Skeleton variant="rounded" height={190} sx={{ borderRadius: '16px' }} />
            </Box>
        );
    }

    const services = items.filter((item) => typeOf(item) === 'service');
    const products = items.filter((item) => typeOf(item) === 'product');
    const itemSheet = itemSheetId === null ? null : items.find((item) => String(lineIdOf(item)) === String(itemSheetId)) || null;
    // O protótipo mostra só o modelo ("Civic"): a API entrega "Honda - Civic".
    const vehicle = usesVehicle && vehicleLabel(details.vehicle_name) ? String(details.vehicle_name).split(/\s+-\s+/).pop() : '';
    const valid = quoteValidUntil(details.quote_date);

    const renderItem = (item) => {
        const isService = typeOf(item) === 'service';
        const qty = Number(item.quantity);
        const sub = isService
            ? (qty > 1 ? `${qty} × R$ ${item.amount_unit}` : '')
            : `${qty} × R$ ${item.amount_unit}${item.item_discount_type ? ` · desc. ${item.item_discount_type}` : ''}`;
        return (
            <KitItem
                key={String(lineIdOf(item))}
                icon={isService ? WrenchIcon : PackageIcon}
                title={item.item_name || item.name}
                sub={sub}
                end={<Box sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.4 }}>R$ {item.amount_total ?? item.total ?? '0,00'}</Box>}
                onClick={editable ? () => setItemSheetId(lineIdOf(item)) : undefined}
            />
        );
    };

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            {/* `.card.grad` */}
            <KitCard sx={{ backgroundImage: theme.palette.gradient.brand, color: '#fff', border: 0, boxShadow: theme.customShadows.md }}>
                <Box sx={{ fontSize: 12.5, opacity: 0.85, lineHeight: 1.4 }}>
                    {[details.customer_name || '—', vehicle].filter(Boolean).join(' · ')}
                </Box>
                <Box sx={{ fontSize: 28, fontWeight: 800, letterSpacing: '-.03em', lineHeight: 1.4 }}>R$ {quoteTotal}</Box>
                {valid && <Box sx={{ fontSize: 12.5, opacity: 0.85, lineHeight: 1.4 }}>Válido até {valid.format('DD/MM')}</Box>}
            </KitCard>

            {converted && (
                <Box sx={{ mt: '12px' }}>
                    <KitBanner tone="info">Este orçamento já teve a Ordem de Serviço gerada — os itens ficam travados para edição.</KitBanner>
                </Box>
            )}

            {loadingItems ? (
                <Skeleton variant="rounded" height={150} sx={{ borderRadius: '16px', mt: '22px' }} />
            ) : items.length === 0 ? (
                <KitEmpty icon={InboxIcon} title="Nenhum item neste orçamento">
                    {editable ? 'Toque em Adicionar item para incluir produtos ou serviços.' : 'Este orçamento não possui itens registrados.'}
                </KitEmpty>
            ) : (
                <>
                    {services.length > 0 && (
                        <>
                            <SecH title="Serviços" />
                            <KitList>{services.map(renderItem)}</KitList>
                        </>
                    )}
                    {products.length > 0 && (
                        <>
                            <SecH title="Peças" />
                            <KitList>{products.map(renderItem)}</KitList>
                        </>
                    )}
                    <KitCard sx={{ mt: '12px' }}>
                        {productDiscount && (
                            <KitKV totals label="Desconto nos produtos" value={`- R$ ${productDiscount}`} />
                        )}
                        <KitKV big label="Total" value={`R$ ${quoteTotal}`} sx={{ border: 0, m: 0, p: 0 }} />
                    </KitCard>
                </>
            )}

            {customerMessages.length > 0 && (
                <>
                    <SecH title="Dúvidas do cliente" />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {customerMessages.map((m) => (
                            <KitCard key={m.id}>
                                <Box sx={{ fontSize: 12, color: 'text.disabled', mb: '4px' }}>{new Date(m.created_at).toLocaleString('pt-BR')}</Box>
                                <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.message}</Box>
                            </KitCard>
                        ))}
                    </Box>
                </>
            )}

            <KitFormBar>
                {editable && (
                    <KitButton onClick={() => setAddOpen(true)}><PlusIcon size={18} /> Adicionar item</KitButton>
                )}
                <KitButton variant="primary" onClick={onConclude}>Concluir</KitButton>
            </KitFormBar>

            {/* Folha "Adicionar item" */}
            <MobileSheet
                open={addOpen}
                onClose={closeAdd}
                title="Adicionar item"
                footer={(
                    <KitButton
                        variant="primary"
                        sx={{ flex: 1 }}
                        disabled={!picked}
                        onClick={() => {
                            const item = picked;
                            closeAdd();
                            onAddItem(item);
                        }}
                    >
                        Adicionar
                    </KitButton>
                )}
            >
                <MobileItemSearch term={term} onTermChange={handleTerm} results={searchResults} picked={picked} onPick={setPicked} />
            </MobileSheet>

            {/* Folha "Ações" (⋯ da barra superior) */}
            <MobileSheet open={actionsOpen} onClose={() => setActionsOpen(false)} title="Ações" actionList>
                <Box>
                    {actions.map((action) => (
                        <ActionItem
                            key={action.key}
                            icon={ACTION_ICONS[action.key] || SwapIcon}
                            tone={action.destructive ? 'red' : undefined}
                            title={action.label}
                            sub={action.sub}
                            disabled={action.disabled}
                            onClick={() => { setActionsOpen(false); action.onClick(); }}
                        />
                    ))}
                </Box>
            </MobileSheet>

            {/* Ações do item (quantidade / desconto / remover) */}
            <MobileSheet open={Boolean(itemSheet)} onClose={() => setItemSheetId(null)} title={itemSheet ? (itemSheet.item_name || itemSheet.name || 'Item') : 'Item'} actionList>
                {itemSheet && (
                    <Box>
                        <ActionItem icon={PlusIcon} title="Aumentar quantidade" sub={`Atual: ${itemSheet.quantity}`} onClick={() => onStep(lineIdOf(itemSheet), 1)} />
                        <ActionItem icon={MinusIcon} title="Diminuir quantidade" sub={`Atual: ${itemSheet.quantity}`} disabled={Number(itemSheet.quantity) <= 1} onClick={() => onStep(lineIdOf(itemSheet), -1)} />
                        {typeOf(itemSheet) === 'product' && (
                            <ActionItem icon={TagIcon} title="Desconto no produto" onClick={() => { const item = itemSheet; setItemSheetId(null); onDiscount(item); }} />
                        )}
                        <ActionItem icon={TrashIcon} tone="red" title="Remover item" onClick={() => { const item = itemSheet; setItemSheetId(null); onRemove(lineIdOf(item)); }} />
                    </Box>
                )}
            </MobileSheet>

            {/* Folha "Alterar status" (menu ⋮ da barra superior) */}
            <MobileSheet open={Boolean(statusSheetOpen)} onClose={onStatusSheetClose} title="Alterar status do orçamento" actionList>
                <Box>
                    {['novo', 'enviado para cliente', 'aguardando aprovacao', 'aprovado', 'ordem gerada'].map((key) => {
                        const view = quoteStatusView(key);
                        return (
                            <ActionItem
                                key={key}
                                icon={SwapIcon}
                                title={view.label}
                                selected={key === statusKey}
                                disabled={!canChangeStatus}
                                onClick={() => { onStatusSheetClose(); if (key !== statusKey) onChangeStatus(key); }}
                            />
                        );
                    })}
                </Box>
            </MobileSheet>
        </Box>
    );
}
